/**
 * Wildebeast premium product gallery
 * ---------------------------------------------------------------------------
 * The swipe gesture itself is native CSS scroll-snap — this only adds the
 * things CSS can't do: thumbnail sync, arrows, keyboard control, variant
 * image jumping, and the zoom lightbox.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class WbGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gal-stage]');
    if (!this.stage) return;

    this.slides = Array.from(this.querySelectorAll('[data-gal-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gal-thumb]'));
    this.prevBtn = this.querySelector('[data-gal-prev]');
    this.nextBtn = this.querySelector('[data-gal-next]');
    this.counter = this.querySelector('[data-gal-current]');
    this.total = this.querySelector('[data-gal-total]');
    this.dots = this.querySelector('[data-gal-dots]');

    this.loop = this.dataset.loop === 'true';
    this.index = 0;

    this.setupObserver();
    this.setupThumbs();
    this.setupArrows();
    this.setupKeyboard();
    this.setupVariantSync();
    this.setupZoom();
    this.setupColourFilter();
    this.renderDots();
    this.detectWhiteBackdrop();

    this.updateUi();
  }

  /**
   * Tracks the visible slide by observing intersection rather than listening
   * to scroll — accurate during momentum scrolling, and far cheaper.
   */
  setupObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index) || index === this.index) return;
          this.index = index;
          this.updateUi();
        });
      },
      { root: this.stage, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  setupThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
    });
  }

  setupArrows() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
  }

  setupKeyboard() {
    this.stage.setAttribute('tabindex', '0');
    this.stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    });
  }

  /**
   * Jumps to the selected variant's image. Listens to both the theme's own
   * variant event and raw form changes, because different Horizon versions
   * (and third-party apps) emit different things.
   */
  setupVariantSync() {
    const jumpToMedia = (mediaId) => {
      if (!mediaId) return;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (target >= 0) this.goTo(target);
    };

    document.addEventListener('variant:update', (event) => {
      jumpToMedia(event.detail?.resource?.featured_media?.id ?? event.detail?.variant?.featured_media?.id);
    });

    document.addEventListener('variant:change', (event) => {
      jumpToMedia(event.detail?.variant?.featured_media?.id);
    });

    // Fallback: watch the variant id input inside the surrounding product form.
    const form = this.closest('form, product-form-component, .product-information, [data-product-form]')
      || document.querySelector('form[action*="/cart/add"]');

    form?.addEventListener('change', async (event) => {
      if (!event.target.matches('input[name="id"], select[name="id"]')) return;
      const variantId = event.target.value;
      if (!variantId) return;

      try {
        const handle = this.dataset.productHandle || window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (!handle) return;
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
        if (!response.ok) return;
        const product = await response.json();
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        jumpToMedia(variant?.featured_media?.id);
      } catch {
        /* Non-critical — the gallery just doesn't auto-jump. */
      }
    });
  }

  goTo(index) {
    const last = this.slides.length - 1;

    if (index < 0) index = this.loop ? last : 0;
    else if (index > last) index = this.loop ? 0 : last;

    const slide = this.slides[index];
    if (!slide) return;

    this.stage.scrollTo({
      left: slide.offsetLeft - this.stage.offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });

    this.index = index;
    this.updateUi();
  }

  updateUi() {
    this.thumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.index;
      thumb.classList.toggle('wb-gal__thumb--active', active);
      thumb.classList.toggle('wb-pdp__thumb--active', active);
      thumb.setAttribute('aria-current', String(active));
      if (active) this.revealThumb(thumb);
    });

    this.dots?.querySelectorAll('button').forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === this.index));
    });
    if (this.total) this.total.textContent = String(this.slides.length);

    this.slides.forEach((slide, i) => {
      // Hide off-screen slides from assistive tech so the gallery reads as
      // one image, not a wall of them.
      if (i === this.index) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    if (this.counter) this.counter.textContent = String(this.index + 1);

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
    }
  }

  /**
   * Scrolls the thumbnail strip so the active thumb is in view. Done on the
   * strip itself — scrollIntoView would also scroll the page.
   */
  revealThumb(thumb) {
    const strip = thumb.parentElement;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const left = thumb.offsetLeft - strip.offsetLeft;
    const right = left + thumb.offsetWidth;
    if (left < strip.scrollLeft) strip.scrollTo({ left, behavior: 'smooth' });
    else if (right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: right - strip.clientWidth, behavior: 'smooth' });
    }
  }

  /**
   * Products shot on white get a plain white frame on every photo. The edge
   * fill is only for lifestyle shots on a coloured backdrop; on a white
   * product it would pull colour in from close-ups that crop through the
   * product. Decided once per product from the first photo's two edge strips.
   */
  detectWhiteBackdrop() {
    const first = this.slides[0];
    if (!first) return;

    const urlOf = (name) => {
      const value = first.style.getPropertyValue(name);
      const match = value.match(/url\(['"]?([^'")]+)['"]?\)/);
      return match ? match[1] : '';
    };

    const brightness = (src) =>
      new Promise((resolve) => {
        if (!src) return resolve(0);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, 0, 0);
            const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
            // Average of each pixel's darkest channel: white stays near 255,
            // cream and beige backdrops drop well below. An average, not a
            // minimum — the strip can clip a few pixels of the product.
            let total = 0;
            for (let i = 0; i < data.length; i += 4) {
              total += Math.min(data[i], data[i + 1], data[i + 2]);
            }
            resolve(total / (data.length / 4));
          } catch {
            resolve(0);
          }
        };
        img.onerror = () => resolve(0);
        img.src = src;
      });

    Promise.all([brightness(urlOf('--wb-slide-l')), brightness(urlOf('--wb-slide-r'))]).then(([left, right]) => {
      if (left >= 238 && right >= 238) this.classList.add('wb-gal--white');
    });
  }

  /** One dot per visible photo — the phone layout's position marker. */
  renderDots() {
    if (!this.dots) return;
    this.dots.replaceChildren();
    this.dots.hidden = this.slides.length < 2;
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `View image ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dots.appendChild(dot);
    });
  }

  /* ---- Colour-matched gallery --------------------------------------------
   * Products sold in more than one colour carry every colour's photos. When
   * a colour is picked, show only that colour's photos (plus any photo that
   * names no colour), and rebuild the thumbnails to match.
   *
   * A photo's colour is read from its alt text, so it needs no extra setup in
   * the admin. Single-colour products are left exactly as they were.
   */

  setupColourFilter() {
    this.allSlides = this.slides.slice();

    const inputs = Array.from(document.querySelectorAll('[data-pdp-option]')).filter((input) =>
      input.closest('.wb-pdp__swatches')
    );
    if (!inputs.length) return;

    this.colourPosition = Number(inputs[0].dataset.pdpOption);
    this.colourValues = Array.from(new Set(inputs.map((input) => input.value))).filter(Boolean);
    if (this.colourValues.length < 2) return;

    const patterns = this.colourValues.map((value) => ({
      value,
      re: new RegExp(`(^|[^a-z])${value.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`),
    }));

    this.allSlides.forEach((slide) => {
      const alt = (slide.querySelector('img')?.getAttribute('alt') || '').toLowerCase();
      const hits = patterns.filter((p) => p.re.test(alt));
      slide.dataset.colour = hits.length === 1 ? hits[0].value : '';
    });

    // Nothing to filter if no photo names a colour.
    if (!this.allSlides.some((slide) => slide.dataset.colour)) return;

    this.thumbLimit = this.querySelectorAll('.wb-pdp__thumb:not(.wb-pdp__thumb--more)').length || 7;

    const checked = inputs.find((input) => input.checked) || inputs[0];
    this.applyColour(checked.value);

    document.addEventListener('variant:change', (event) => {
      const colour = event.detail?.variant?.options?.[this.colourPosition - 1];
      if (colour) this.applyColour(colour);
    });
  }

  applyColour(colour) {
    if (!this.allSlides || colour === this.activeColour) return;

    const visible = this.allSlides.filter(
      (slide) => !slide.dataset.colour || slide.dataset.colour === colour
    );
    if (!visible.length) return;
    this.activeColour = colour;

    this.allSlides.forEach((slide) => {
      const show = visible.includes(slide);
      slide.style.display = show ? '' : 'none';
      slide.dataset.index = show ? String(visible.indexOf(slide)) : '-1';
      if (!show) this.observer?.unobserve(slide);
      else this.observer?.observe(slide);
    });
    this.slides = visible;

    this.rebuildThumbs();
    this.renderDots();

    this.index = 0;
    this.stage.scrollTo({ left: 0, behavior: 'auto' });
    this.updateUi();
  }

  thumbSrc(slide, width) {
    const img = slide.querySelector('img');
    const raw = img?.getAttribute('src') || img?.currentSrc || '';
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return raw;
    }
  }

  makeThumb(className, index, width, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.dataset.galThumb = '';
    button.dataset.index = String(index);
    if (label) button.setAttribute('aria-label', label);

    const img = document.createElement('img');
    img.src = this.thumbSrc(this.slides[index], width);
    img.alt = '';
    img.loading = 'lazy';
    img.width = width;
    img.height = Math.round((width * 4) / 3);
    button.appendChild(img);
    button.addEventListener('click', () => this.goTo(index));
    return button;
  }

  rebuildThumbs() {
    const strip = this.querySelector('.wb-pdp__thumbs');
    const rail = this.querySelector('.wb-pdp__side');

    if (strip) {
      strip.replaceChildren();
      const count = Math.min(this.thumbLimit, this.slides.length);
      for (let i = 0; i < count; i += 1) {
        strip.appendChild(this.makeThumb('wb-pdp__thumb', i, 260, `View image ${i + 1}`));
      }
      const extra = this.slides.length - this.thumbLimit;
      if (extra > 0) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'wb-pdp__thumb wb-pdp__thumb--more';
        more.dataset.galThumb = '';
        more.dataset.index = String(this.thumbLimit);
        more.textContent = `+${extra}`;
        more.addEventListener('click', () => this.goTo(this.thumbLimit));
        strip.appendChild(more);
      }
      strip.hidden = this.slides.length < 2;
    }

    if (rail) {
      rail.replaceChildren();
      this.slides.slice(1, 4).forEach((slide, i) => {
        rail.appendChild(this.makeThumb('wb-pdp__side-thumb', i + 1, 500, ''));
      });
    }

    // Fallback for other gallery layouts: hide thumbs for hidden photos.
    if (!strip && !rail) {
      this.querySelectorAll('[data-gal-thumb]').forEach((thumb) => {
        if (thumb.dataset.mediaIndex === undefined) thumb.dataset.mediaIndex = thumb.dataset.index;
        const slide = this.allSlides[Number(thumb.dataset.mediaIndex)];
        const show = slide && slide.style.display !== 'none';
        thumb.style.display = show ? '' : 'none';
        if (show) thumb.dataset.index = slide.dataset.index;
      });
    }

    this.thumbs = Array.from(this.querySelectorAll('[data-gal-thumb]')).filter(
      (thumb) => thumb.style.display !== 'none' && !thumb.classList.contains('wb-pdp__thumb--more')
    );
  }

  /* ---- Zoom lightbox ---------------------------------------------------- */

  setupZoom() {
    if (this.dataset.zoom !== 'true') return;

    // The lightbox is emitted by the same block or section, but not always as
    // a direct sibling: the premium gallery block renders it next to this
    // element, while wb-product-main renders it at the section level. Walk
    // outward — parent first, then the enclosing section, then the document —
    // so both layouts find it.
    this.lightbox = this.parentElement?.querySelector('[data-gal-lightbox]')
      || this.closest('section')?.querySelector('[data-gal-lightbox]')
      || document.querySelector('[data-gal-lightbox]');
    if (!this.lightbox) return;

    this.lightboxImg = this.lightbox.querySelector('[data-gal-lightbox-img]');
    this.canvas = this.lightbox.querySelector('[data-gal-lightbox-canvas]');

    this.querySelectorAll('[data-gal-zoom]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const slide = trigger.closest('[data-gal-slide]');
        const index = this.slides.indexOf(slide);
        this.openZoom(index >= 0 ? index : this.index);
      });
    });

    this.setupZoomNav();

    this.lightbox.querySelector('[data-gal-lightbox-close]')?.addEventListener('click', () => this.closeZoom());

    // Pointer capture retargets the release click to the canvas even when the
    // press began on the image or ended after a pan, so event.target can't be
    // trusted here. Close only for a genuine backdrop tap: the press started
    // on the empty canvas itself and never travelled.
    this.canvas?.addEventListener('click', () => {
      if (this._pressOnBackdrop && !this._pressMoved) this.closeZoom();
    });

    this.onZoomKey = (event) => {
      if (event.key === 'Escape') this.closeZoom();
      else if (event.key === 'ArrowRight') this.stepZoom(1);
      else if (event.key === 'ArrowLeft') this.stepZoom(-1);
    };

    this.setupPanAndZoom();
  }

  /**
   * Previous / next arrows and a counter inside the zoom view, created here
   * so the section markup needs no change. One image at a time; the shopper
   * moves with the arrows, the arrow keys, a sideways trackpad swipe or a
   * finger swipe.
   */
  setupZoomNav() {
    if (this.lightbox.querySelector('[data-gal-lightbox-prev]')) return;

    const makeButton = (dir, label, path) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `wb-gal-lightbox__nav wb-gal-lightbox__nav--${dir}`;
      button.setAttribute('aria-label', label);
      button.dataset[dir === 'prev' ? 'galLightboxPrev' : 'galLightboxNext'] = '';
      button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.stepZoom(dir === 'prev' ? -1 : 1);
      });
      this.lightbox.appendChild(button);
      return button;
    };

    this.zoomPrev = makeButton('prev', 'Previous image', 'M15 5l-7 7 7 7');
    this.zoomNext = makeButton('next', 'Next image', 'M9 5l7 7-7 7');

    this.zoomCount = document.createElement('p');
    this.zoomCount.className = 'wb-gal-lightbox__count';
    this.zoomCount.setAttribute('aria-live', 'polite');
    this.lightbox.appendChild(this.zoomCount);
  }

  zoomSlides() {
    // Only photos can be zoomed — skip videos and 3D models.
    return this.slides.filter((slide) => slide.querySelector('[data-gal-zoom]'));
  }

  showZoom(index) {
    const slides = this.zoomSlides();
    if (!slides.length) return;
    const count = slides.length;
    this.zoomIndex = ((index % count) + count) % count;

    const trigger = slides[this.zoomIndex].querySelector('[data-gal-zoom]');
    this.lightboxImg.src = trigger.dataset.full;
    this.lightboxImg.alt = trigger.querySelector('img')?.alt || '';
    this.resetTransform();

    if (this.zoomCount) this.zoomCount.textContent = `${this.zoomIndex + 1} / ${count}`;
    const single = count < 2;
    if (this.zoomPrev) this.zoomPrev.hidden = single;
    if (this.zoomNext) this.zoomNext.hidden = single;
  }

  stepZoom(delta) {
    if (!this.lightbox || this.lightbox.hidden) return;
    this.showZoom(this.zoomIndex + delta);
  }

  openZoom(slideIndex) {
    if (!this.lightbox) return;

    const slide = this.slides[slideIndex];
    const start = Math.max(0, this.zoomSlides().indexOf(slide));

    // Belt and braces: guarantee the image opens fitted to the screen even if
    // the stylesheet is stale or missing on this page — inline styles cannot
    // be cached out or overridden. Viewport units, not percentages: the
    // canvas's grid row is content-sized, so a percentage cap resolved to
    // nothing and the photo opened pre-zoomed and cut off.
    Object.assign(this.lightboxImg.style, {
      maxWidth: '92vw',
      maxHeight: '86vh',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    });

    this.showZoom(start);

    this.lightbox.hidden = false;
    // Next frame, so the opacity transition actually runs.
    requestAnimationFrame(() => {
      this.lightbox.dataset.open = 'true';
    });

    document.documentElement.classList.add('wb-scroll-locked');
    document.addEventListener('keydown', this.onZoomKey);
    this.lightbox.querySelector('[data-gal-lightbox-close]')?.focus();
  }

  closeZoom() {
    if (!this.lightbox || this.lightbox.hidden) return;

    this.lightbox.dataset.open = 'false';
    document.documentElement.classList.remove('wb-scroll-locked');
    document.removeEventListener('keydown', this.onZoomKey);

    const hide = () => {
      this.lightbox.hidden = true;
      this.lightboxImg.src = '';
    };
    if (REDUCED_MOTION.matches) hide();
    else setTimeout(hide, 260);

    // Leave the page gallery on the photo the shopper ended on.
    const last = this.zoomSlides()[this.zoomIndex];
    const pageIndex = last ? this.slides.indexOf(last) : -1;
    if (pageIndex >= 0 && pageIndex !== this.index) this.goTo(pageIndex);

    this.slides[this.index]?.querySelector('[data-gal-zoom]')?.focus();
  }

  resetTransform() {
    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.applyTransform();
  }

  applyTransform() {
    if (!this.lightboxImg) return;
    this.lightboxImg.style.translate = `${this.offsetX}px ${this.offsetY}px`;
    this.lightboxImg.style.scale = String(this.scale);
  }

  setupPanAndZoom() {
    if (!this.canvas) return;

    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let pressX = 0;
    let pressY = 0;

    // Wheel / trackpad zoom, clamped so the image can't be lost off-screen.
    // Instant — no transition. Animating every wheel tick makes the zoom lag
    // behind the gesture and feel rubbery; the 200ms ease belongs only to the
    // double-click toggle, applied via data-animate.
    this.canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();

        // A sideways trackpad swipe at normal size changes photo; one change
        // per gesture, then a short pause so momentum doesn't skip several.
        if (this.scale === 1 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          if (this._swipeLock) return;
          this._swipeX = (this._swipeX || 0) + event.deltaX;
          if (Math.abs(this._swipeX) > 40) {
            this.stepZoom(this._swipeX > 0 ? 1 : -1);
            this._swipeX = 0;
            this._swipeLock = true;
            setTimeout(() => {
              this._swipeLock = false;
            }, 450);
          }
          return;
        }

        delete this.lightboxImg.dataset.animate;
        const next = this.scale - event.deltaY * 0.002;
        this.scale = Math.min(4, Math.max(1, next));
        if (this.scale === 1) {
          this.offsetX = 0;
          this.offsetY = 0;
        }
        this.applyTransform();
      },
      { passive: false }
    );

    // Double-click / double-tap toggles between fit and 2× — the one place
    // the scale animation is wanted.
    this.canvas.addEventListener('dblclick', () => {
      this.lightboxImg.dataset.animate = 'true';
      this.scale = this.scale > 1 ? 1 : 2;
      if (this.scale === 1) {
        this.offsetX = 0;
        this.offsetY = 0;
      }
      this.applyTransform();
      setTimeout(() => delete this.lightboxImg.dataset.animate, 240);
    });

    const start = (x, y) => {
      dragging = true;
      startX = x - this.offsetX;
      startY = y - this.offsetY;
    };

    const move = (x, y) => {
      if (!dragging || this.scale === 1) return;
      this.offsetX = x - startX;
      this.offsetY = y - startY;
      this.applyTransform();
    };

    const end = (event) => {
      // A finger or mouse swipe at normal size changes photo.
      if (dragging && this.scale === 1 && event?.type === 'pointerup') {
        const dx = event.clientX - pressX;
        const dy = event.clientY - pressY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepZoom(dx < 0 ? 1 : -1);
      }
      dragging = false;
    };

    this.canvas.addEventListener('pointerdown', (e) => {
      // Remember where and on what the press began — the click handler needs
      // this because pointer capture retargets the eventual click to the
      // canvas regardless of the true press target.
      this._pressOnBackdrop = e.target === this.canvas;
      this._pressMoved = false;
      pressX = e.clientX;
      pressY = e.clientY;
      delete this.lightboxImg.dataset.animate;
      this.canvas.setPointerCapture(e.pointerId);
      start(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (dragging && Math.hypot(e.clientX - pressX, e.clientY - pressY) > 4) {
        this._pressMoved = true;
      }
      move(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('pointerup', end);
    this.canvas.addEventListener('pointercancel', end);

    // Pinch to zoom on touch.
    let pinchStart = null;
    this.canvas.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 2) return;
        const [a, b] = event.touches;
        pinchStart = { dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY), scale: this.scale };
      },
      { passive: true }
    );

    this.canvas.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 2 || !pinchStart) return;
        event.preventDefault();
        delete this.lightboxImg.dataset.animate;
        const [a, b] = event.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        this.scale = Math.min(4, Math.max(1, pinchStart.scale * (dist / pinchStart.dist)));
        this.applyTransform();
      },
      { passive: false }
    );

    this.canvas.addEventListener('touchend', () => {
      pinchStart = null;
    });
  }
}

if (!customElements.get('wb-gallery')) customElements.define('wb-gallery', WbGallery);
